// INSIDO: instance admin panel data layer, see mvp-fase1/modulos/05-painel-admin.
// The upstream API has no instance-wide listing endpoints, so the snapshot is
// aggregated from the organizations the instance admin is a member of.
import type { Client, Server, User } from "stoat.js";

/** User flags bitfield, mirrors upstream `UserFlags` (crates/core/models). */
export enum UserFlags {
  Suspended = 1,
  Deleted = 2,
  Banned = 4,
  Spam = 8,
}

export interface AdminOrganizationEntry {
  server: Server;
  memberCount: number;
}

export interface AdminUserEntry {
  user: User;
  /** Organizations (visible to the admin) this user belongs to */
  organizations: Server[];
}

export interface AdminSnapshot {
  organizations: AdminOrganizationEntry[];
  users: AdminUserEntry[];
  /** Organizations whose member list could not be fetched */
  failedOrganizations: Server[];
  totalChannels: number;
  fetchedAt: Date;
}

/**
 * Aggregate users and member counts across every organization the
 * authenticated admin belongs to. Requests run sequentially to stay
 * clear of the API rate limiter.
 */
export async function fetchAdminSnapshot(
  client: Client,
): Promise<AdminSnapshot> {
  const servers = client.servers.toList();
  const organizations: AdminOrganizationEntry[] = [];
  const failedOrganizations: Server[] = [];
  const usersById = new Map<string, AdminUserEntry>();
  let totalChannels = 0;

  for (const server of servers) {
    try {
      const { members, users } = await client.api.get(
        `/servers/${server.id as ""}/members`,
        { exclude_offline: false },
      );

      organizations.push({ server, memberCount: members.length });
      totalChannels += server.channels.length;

      for (const apiUser of users) {
        // hydrate into the client cache so we can reuse reactive accessors
        const user = client.users.getOrCreate(apiUser._id, apiUser);
        const entry = usersById.get(user.id);

        if (entry) {
          entry.organizations.push(server);
        } else {
          usersById.set(user.id, { user, organizations: [server] });
        }
      }
    } catch {
      failedOrganizations.push(server);
    }
  }

  return {
    organizations,
    users: [...usersById.values()].sort((a, b) =>
      a.user.username.localeCompare(b.user.username),
    ),
    failedOrganizations,
    totalChannels,
    fetchedAt: new Date(),
  };
}

/**
 * Fetch a user's current flags straight from the API.
 * Returns the bitfield, see {@link UserFlags}.
 */
export async function fetchUserFlags(
  client: Client,
  userId: string,
): Promise<number> {
  const response = await client.api.get(`/users/${userId as ""}/flags`);
  return response.flags;
}
